import React, { useState, useEffect, useRef } from 'react'
import { cornerData, floorSize, theColor } from './utils/helpers'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { createCircleMesh } from './Player'
// Add a subtle environment map for reflections
function EnvironmentMap() {
  return (
    <React.Suspense fallback={null}>
      <ambientLight intensity={0.2} />
      <directionalLight position={[5, 10, 7]} intensity={0.8} color="#fff9e8" />
      <directionalLight position={[-5, 3, -7]} intensity={0.2} color="#b0c4de" />
    </React.Suspense>
  )
}

// Enhanced Floor with subtle pattern
function Floor() {
  // Create a simple repeating pattern in the shader
  const floorShader = {
    uniforms: {
      color: { value: new THREE.Color("#444444") },
      gridColor: { value: new THREE.Color("#555555") },
      subGridColor: { value: new THREE.Color("#4A4A4A") }, // Color for subdivision lines
      gridSize: { value: floorSize / 10 },
      subDivisions: { value: 2.0 }, // Number of subdivisions per grid cell
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 color;
      uniform vec3 gridColor;
      uniform vec3 subGridColor;
      uniform float gridSize;
      uniform float subDivisions;
      varying vec2 vUv;
      
      void main() {
        // Scale UVs to create main grid
        vec2 grid = fract(vUv * gridSize);
        
        // Create soft grid pattern for main lines
        float mainLine = smoothstep(0.03, 0.05, min(grid.x, grid.y)) * 
                         smoothstep(0.03, 0.05, min(1.0-grid.x, 1.0-grid.y));
        
        // Create subdivision grid - scale by subDivisions
        vec2 subGrid = fract(vUv * gridSize * subDivisions);
        
        // Create thinner lines for subdivisions
        float subLine = smoothstep(0.01, 0.02, min(subGrid.x, subGrid.y)) * 
                        smoothstep(0.01, 0.02, min(1.0-subGrid.x, 1.0-subGrid.y));
        
        // Combine both grids - main grid takes precedence
        float combinedGrid = mix(subLine, mainLine, step(0.95, mainLine));
        
        // Mix floor color with grid colors
        vec3 subGridFinal = mix(subGridColor, color, subLine * 0.9 + 0.1);
        vec3 finalColor = mix(gridColor, subGridFinal, mainLine * 0.8 + 0.2);
        
        gl_FragColor = vec4(finalColor, 1.0);
      }
    `
  };

    return (
      <mesh 
        rotation={[-Math.PI / 2, 0, 0]} 
        position={[0, 0, 0]} 
    >
      <planeGeometry args={[floorSize, floorSize, 1, 1]} />
      <shaderMaterial
        attach="material"
        args={[floorShader]}
        uniforms-color-value={new THREE.Color("#444444")}
        uniforms-gridColor-value={new THREE.Color("#555555")}
        uniforms-subGridColor-value={new THREE.Color("#4A4A4A")}
        uniforms-gridSize-value={floorSize / 10}
        uniforms-subDivisions-value={5.0}
      />
      </mesh>
    )
  }
  
// Improved Wall Component with subtle gradient
function Walls() {
  const wallHeight = 5;
  const wallColor = "#000";
  const halfFloorSize = floorSize / 2;
  const wallOpacity = 0.4;

  // Define wall properties
  const walls = [
    { position: [0, wallHeight / 2, -halfFloorSize], rotation: [0, 0, 0] }, // North Wall
    { position: [halfFloorSize, wallHeight / 2, 0], rotation: [0, Math.PI / 2, 0] }, // East Wall
  ];

  return (
    <>
      {walls.map((wall, index) => (
        <mesh
          key={index}
          position={wall.position}
          rotation={wall.rotation}
        >
          <boxGeometry args={[floorSize, wallHeight, 0.1]} />
          <meshBasicMaterial
            color={wallColor}
            transparent
            opacity={wallOpacity}
            roughness={0.3}
            metalness={0.1}
            clearcoat={0.1}
            clearcoatRoughness={0.2}
          />
        </mesh>
      ))}
    </>
  );
}

// RestrictedArea with pulsing effect (very lightweight animation)
function RestrictedArea({ restrictedRadius }) {
  const segments = 32; // Higher for smoother circle
  const restrictedRadiusMinus = restrictedRadius - 1.9;


    return (
    <mesh
      rotation={[0, 0, 0]}
      position={[0, 0.03, 0]}
    >
      <cylinderGeometry args={[restrictedRadiusMinus, restrictedRadiusMinus, 0.2, segments]} />
      <meshBasicMaterial
        color="white"
        transparent
        opacity={0.4}
        side={THREE.DoubleSide}
      />
      </mesh>
  );
}

// Create an animated waves shader for the active corner with waves from center
function WavesCorner({ corner, isActive, useShaders = true }) {
  const materialRef = useRef();
  const meshRef = useRef();

  // Simple animation update
  useFrame(({ clock }) => {
    if (materialRef.current && isActive) {
      // Update time for wave animation
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
    }
  });

  // Create the performant wave shader with center-based waves
  const waveShader = {
    uniforms: {
      baseColor: { value: new THREE.Color(theColor) },
      uTime: { value: 0 },
      cornerIndex: { value: corner.index }, // Pass corner index to shader
      floorHalfSize: { value: floorSize / 4 } // Half size of corner quadrant
    },
    vertexShader: `
      varying vec2 vUv;
      
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 baseColor;
      uniform float uTime;
      uniform int cornerIndex;
      uniform float floorHalfSize;
      varying vec2 vUv;
      
      // Fast approximation of sin(x) - more performant than using sin()
      float fastSin(float x) {
        x = mod(x, 6.28318); // Keep x between 0 and 2π
        float x2 = x * x;
        // Simple polynomial approximation
        return x * (1.0 - x2 / 20.0);
      }
      
      void main() {
        // Transform UV coordinates (0-1) to corner-relative coordinates
        vec2 cornerUV = vUv;
        
        // Calculate center point coordinates based on corner index
        vec2 centerPoint;
        
        // Corner indices: 0=top-right, 1=top-left, 2=bottom-left, 3=bottom-right
        if (cornerIndex == 3) {
          centerPoint = vec2(0.0, 0.0);
        } else if (cornerIndex == 2) {
          centerPoint = vec2(1.0, 0.0);
        } else if (cornerIndex == 1) {
          centerPoint = vec2(1.0, 1.0);
        } else {
          centerPoint = vec2(0.0, 1.0);
        }
        
        // Calculate distance from center point
        float dist = length(cornerUV - centerPoint) * 3.0; // Scale for more visible effect
        
        // Create circular wave pattern from center - increased speed
        float wave = fastSin(dist * 5.0 - uTime * 3.5) * 0.5 + 0.5; // Faster waves
        
        // Use only the circular wave pattern
        float pattern = wave * (1.0 - dist * 0.2);
        
        // Create final color with a brighter version of baseColor
        vec3 highlightColor = baseColor * 1.2; // Make the highlight 20% brighter
        vec3 finalColor = mix(baseColor, highlightColor, pattern * 0.2);
        
        gl_FragColor = vec4(finalColor, 1.0);
      }
    `
  };

  return (
    <mesh
      ref={meshRef}
      position={corner.position}
      rotation={[-Math.PI / 2, 0, 0]}
    >
      <planeGeometry args={[floorSize/2, floorSize/2, 1, 1]} />
      {useShaders ? (
        <shaderMaterial
          ref={materialRef}
          args={[waveShader]}
          transparent
          uniforms-baseColor-value={new THREE.Color(theColor)}
          uniforms-cornerIndex-value={corner.index}
          uniforms-floorHalfSize-value={floorSize / 4}
          side={THREE.DoubleSide}
        />
      ) : (
        <meshBasicMaterial
          color={theColor}
          transparent
          opacity={0}
        />
      )}
    </mesh>
  );
}

// FadeOut Shader Component for previous corner
function FadeOutCorner({ corner, startTime }) {
  const materialRef = useRef();

  // Setup the fade shader
  const fadeShader = {
    uniforms: {
      baseColor: { value: new THREE.Color(theColor) },
      uTime: { value: 0 },
      uStartTime: { value: startTime || 0 },
      uFadeDuration: { value: 3.0 } // Duration of fade in seconds
    },
    vertexShader: `
      varying vec2 vUv;
      
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 baseColor;
      uniform float uTime;
      uniform float uStartTime;
      uniform float uFadeDuration;
      varying vec2 vUv;
      
      void main() {
        // Calculate fade progress (0 to 1)
        float elapsed = max(0.0, uTime - uStartTime);
        float fadeProgress = min(elapsed / uFadeDuration, 1.0);
        
        // Calculate opacity (from 0.8 down to 0)
        float opacity = 0.8 * (1.0 - fadeProgress);
        
        // Output final color with calculated opacity
        gl_FragColor = vec4(baseColor, opacity);
      }
    `
  };

  // Update time uniform on each frame
  useFrame(({ clock }) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
    }
  });

  return (
    <mesh
      position={corner.position}
      rotation={[-Math.PI / 2, 0, 0]}
    >
      <planeGeometry args={[floorSize/2, floorSize/2]} />
      <shaderMaterial
        ref={materialRef}
        args={[fadeShader]}
        transparent={true}
        uniforms-baseColor-value={new THREE.Color(theColor)}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

// Enhanced SkyPanels with pastel colors
function SkyPanels() {
  // Define a set of pastel colors outside of any render/effect loops
  const pastelColors = [
    new THREE.Color("#FFB6C1"), // Light Pink
    new THREE.Color("#ADD8E6"), // Light Blue
    new THREE.Color("#90EE90"), // Light Green
    new THREE.Color("#FFFFE0"), // Light Yellow
    new THREE.Color("#E6E6FA"), // Lavender
    new THREE.Color("#FFC0CB"), // Pink
    new THREE.Color("#B0E0E6"), // Powder Blue
    new THREE.Color("#F0E68C"), // Khaki
    new THREE.Color("#D8BFD8"), // Thistle
    new THREE.Color("#98FB98")  // Pale Green
  ];
  
  // Increase panel count but keep optimization techniques
  const panelCount = 60; // Distant panels
  const nearPanelCount = 5; // Additional panels near the floor
  const panelDistance = 200; // Far panels
  const nearDistance = 20; // Near panels
  const panelsRef = useRef([]);
  
  // Generate both distant and nearby panels
  useEffect(() => {
    // Generate distant sky panels
    const distantPanels = Array(panelCount).fill().map((_, index) => ({
      position: [
        (Math.random() - 0.5) * panelDistance * 2, // X position
        Math.random() * panelDistance * 0.6 + panelDistance * 0.2, // Y position (high in sky)
        (Math.random() - 0.5) * panelDistance * 4 // Z position
      ],
      rotation: [0, Math.random() * Math.PI, 0], // Y-axis rotation only
      scale: Math.random() * 3 + 2, // Size between 2-5
      color: pastelColors[index % pastelColors.length], // Assign color by index
      isNear: false
    }));
    
    // Generate nearby panels (around the floor area)
    const floorAreaSize = floorSize * 1.5; // Area around the floor
    const nearbyPanels = Array(nearPanelCount).fill().map((_, index) => ({
      position: [
        (Math.random() - 0.5) * floorAreaSize, // X position near floor area
        Math.random() * 15 + 5, // Y position (5-20 units above floor)
        (Math.random() - 0.5) * floorAreaSize // Z position near floor area
      ],
      rotation: [0, Math.random() * Math.PI, 0], // Y-axis rotation only
      scale: Math.random() * 2 + 1, // Smaller size for near panels (1-3)
      color: pastelColors[(index + panelCount) % pastelColors.length], // Assign color by index
      isNear: true
    }));
    
    // Combine both sets of panels
    panelsRef.current = [...distantPanels, ...nearbyPanels];
  }, []);

  // Use a single animated value instead of per-panel animation (for performance)
  const timeRef = useRef(0);
  
  // Simplified animation with less computation
  useFrame(({ clock }) => {
    timeRef.current = clock.getElapsedTime() * 0.2;
  });

  return (
    <group>
      {panelsRef.current.map((panel, index) => {
        // Calculate animation amplitude based on whether it's a near or far panel
        const amplitude = panel.isNear ? 0.3 : 0.5;
        // Use different animation speeds for near vs far panels
        const animationSpeed = panel.isNear ? 0.08 : 0.05;
        
        return (
          <mesh
            key={index}
            position={[
              panel.position[0],
              panel.position[1] + Math.sin(timeRef.current + index * 0.3) * amplitude, 
              panel.position[2]
            ]}
            rotation={[0, panel.rotation[1] + timeRef.current * animationSpeed, 0]}
            scale={[panel.scale  * 3.5, panel.scale, 0.1]}
          >
            <planeGeometry args={[1, 1]} />
            <meshBasicMaterial
              color={panel.color}
              transparent={true}
              opacity={panel.isNear ? 0.3 : 1} // Slightly more transparent for near panels
              side={THREE.DoubleSide}
            />
          </mesh>
        );
      })}
    </group>
  );
}

export function Club({ playerPosition, setIsInFloor, cornerIndexCurrent, setCornerIndexCurrent, cornerIndexNext, setCornerIndexNext, restrictedRadius, useShaders = true, showTutorial }) {
    const changeEvery = 6000;
    const blinkBefore = changeEvery * 0.2;
    const blinkOpacity = 0.4;

    const [cornerIndexPrev, setCornerIndexPrev] = useState(null);
    const [cornerIndexNextOpacity, setCornerIndexNextOpacity] = useState(0);
    const [cornerIndexPrevOpacity, setCornerIndexPrevOpacity] = useState(0);
    // Add state for NPCs
    const [npcs, setNpcs] = useState([]);

    // Track when the fade starts for each previous corner
    const [fadeStartTime, setFadeStartTime] = useState(0);
    const clockRef = useRef({ getElapsedTime: () => 0 });

    // Generate random NPCs on initialization
    useEffect(() => {
      const npcCount = 30; // Number of NPCs to generate
      const halfFloorSize = floorSize / 2;
      const safeArea = restrictedRadius - 2; // Keep NPCs away from DJ booth/center
      
      // Define which corner should have fewer NPCs (0 = top-right)
      const sparseCornerIndex = 1;
      const sparseCorner = cornerData[sparseCornerIndex];
      const cornerPosition = sparseCorner.position;
      const avoidanceRadius = floorSize / 4; // Distance to avoid around the corner
      
      const randomNpcs = Array(npcCount).fill().map((_, index) => {
        // Generate random position within floor bounds, but keep away from center
        let x, z;
        let inSparseCorner;
        
        do {
          x = (Math.random() * 2 - 1) * (halfFloorSize - 5);
          z = (Math.random() * 2 - 1) * (halfFloorSize - 5);
          
          // Check if position is in restricted center area
          const tooCloseToCenter = Math.sqrt(x*x + z*z) < restrictedRadius + safeArea;
          
          // Check if position is in the sparse corner area
          // Need to rotate coordinates since the corners are in a rotated system
          const cos = Math.cos(Math.PI / 4);
          const sin = Math.sin(Math.PI / 4);
          const rotatedX = x * cos - z * sin;
          const rotatedZ = x * sin + z * cos;
          
          // Calculate distance to the sparse corner
          const cornerX = cornerPosition[0];
          const cornerZ = cornerPosition[2];
          const distToCorner = Math.sqrt(
            Math.pow(rotatedX - cornerX, 2) + 
            Math.pow(rotatedZ - cornerZ, 2)
          );
          
          inSparseCorner = distToCorner < avoidanceRadius;
          
          // 80% chance to reject if in sparse corner
          const rejectFromSparseCorner = inSparseCorner && Math.random() < 0.8;
          
        } while (Math.sqrt(x*x + z*z) < restrictedRadius + safeArea || 
                (inSparseCorner && Math.random() < 0.95)); // 80% chance to reject if in sparse corner
        
        // Calculate angle to center for rotation
        const angleToCenter = Math.atan2(-x, -z); // Face toward center (0,0,0)
        
        return {
          position: [x, 0.5, z], // Y is 0.5 for floor level
          rotation: angleToCenter, // Rotation to face center,
          isEven: index % 2 === 0
        };
      });
      
      setNpcs(randomNpcs);
    }, [restrictedRadius]); // Only run once on initialization

    // Get clock reference for tracking time
    useFrame(({ clock }) => {
      clockRef.current = clock;
    });

    // Memoize the isPlayerInCorner function to avoid recalculating unnecessarily
    const isPlayerInCorner = React.useCallback((cornerIndex) => {
      if (!playerPosition || cornerIndex === null) return false;
      
      const [playerX, playerY, playerZ] = playerPosition;
      const corner = cornerData[cornerIndex];
      const [cornerX, cornerY, cornerZ] = corner.position;
      
      const cornerHalfSize = floorSize / 4;
      
      // Pre-calculate cos and sin values once
      const cos = Math.cos(Math.PI / 4);
      const sin = Math.sin(Math.PI / 4);

      // Transformed coordinates
      const rotatedPlayerX = playerX * cos - playerZ * sin;
      const rotatedPlayerZ = playerX * sin + playerZ * cos;
      
      return (
        Math.abs(rotatedPlayerX - cornerX) < cornerHalfSize &&
        Math.abs(rotatedPlayerZ - cornerZ) < cornerHalfSize
      );
    }, [playerPosition]);

    // Update isInFloor whenever player position or active corners change
    useEffect(() => {
      if (cornerIndexCurrent >= 0){
      const isOnCurrentCorner = isPlayerInCorner(cornerIndexCurrent);
      const isOnPrevCorner = cornerIndexPrev !== null && 
                             cornerIndexPrevOpacity > 0 && 
                             isPlayerInCorner(cornerIndexPrev);
      
      setIsInFloor(isOnCurrentCorner || isOnPrevCorner);
      }
    }, [playerPosition, cornerIndexCurrent, cornerIndexPrev, cornerIndexPrevOpacity, setIsInFloor, isPlayerInCorner]);

    // REMOVE the old stepped fadeout effect and replace with this:
    useEffect(() => {
      if (cornerIndexPrev !== null) {
        // Record the start time for fading
        setFadeStartTime(clockRef.current.getElapsedTime());

        // Set initial opacity for player detection
        setCornerIndexPrevOpacity(0.8);
        
        // Gradually reduce opacity for player detection
        const timer1 = setTimeout(() => setCornerIndexPrevOpacity(0.5), changeEvery / 6);
        const timer2 = setTimeout(() => setCornerIndexPrevOpacity(0.3), changeEvery / 3);
        const timer3 = setTimeout(() => setCornerIndexPrevOpacity(0), changeEvery / 2);

        return () => {
          clearTimeout(timer1);
          clearTimeout(timer2);
          clearTimeout(timer3);
        };
      }
    }, [cornerIndexPrev, changeEvery]);

    // Optimized corner change and blinking logic
    useEffect(() => {
      let blinkTimeout = null;
      let changeTimeout = null;

      // Only start corner changing if showTutorial is false
      if (!showTutorial) {
        const startCornerChange = () => {
          // Determine next corner
        let newIndex;
        if (cornerIndexCurrent === 0) {
            newIndex = Math.random() < 0.5 ? 1 : 3;
        } else if (cornerIndexCurrent === 3) {
            newIndex = Math.random() < 0.5 ? 2 : 0;
        } else {
            newIndex = cornerIndexCurrent + (Math.random() < 0.5 ? -1 : 1);
        }
        
          // Simplified blinking with fewer updates
          const totalBlinks = 4; // Reduce number of blinks
          let blinkCount = 0;

          const doBlink = (visible) => {
        setCornerIndexNext(newIndex);
            setCornerIndexNextOpacity(visible ? blinkOpacity : 0);

            blinkCount++;

            if (blinkCount >= totalBlinks) {
              setCornerIndexNextOpacity(0);
              setCornerIndexPrev(cornerIndexCurrent);
              setCornerIndexCurrent(newIndex);

              // Schedule next change
              changeTimeout = setTimeout(startCornerChange, changeEvery - blinkBefore);
            } else {
              // Longer interval between blinks (250ms instead of 150ms)
              blinkTimeout = setTimeout(() => doBlink(!visible), 250);
            }
          };

          // Start blinking
          blinkTimeout = setTimeout(() => doBlink(true), 100);
        };

        // Start the initial corner change cycle
        changeTimeout = setTimeout(startCornerChange, changeEvery - blinkBefore);
      }

      return () => {
        clearTimeout(blinkTimeout);
        clearTimeout(changeTimeout);
      };
    }, [cornerIndexCurrent, blinkBefore, blinkOpacity, changeEvery, showTutorial]); // Include showTutorial in dependencies

    return (
        <mesh rotation={[0, Math.PI / 4, 0]}>
        {/* Add environment lighting */}
        <EnvironmentMap />

        {/* Only include SkyPanels if shaders are enabled (indicating higher performance) */}
        {useShaders && <SkyPanels />}

        <Floor />
        <Walls />
        
        {/* Normal corners with standard materials */}
        {cornerData.map((corner, index) => (
          index !== cornerIndexCurrent ? (
          <mesh 
            key={index}
            position={corner.position} 
            rotation={[-Math.PI / 2, 0, 0]} 
          > 
              <planeGeometry args={[floorSize/2, floorSize/2]} />
            <meshBasicMaterial 
              color={theColor}
              transparent 
                opacity={index === cornerIndexNext ? cornerIndexNextOpacity : 0}
              />
            </mesh>
          ) : (
            // Apply the shader only when it's the current corner and not blinking
            <WavesCorner
              key={index}
              corner={{...corner, index}}
              isActive={true}
              useShaders={useShaders}
            />
          )
        ))}

        {/* Previous corner with shader-based fadeout */}
        {cornerIndexPrev !== null && (
          <FadeOutCorner
            corner={cornerData[cornerIndexPrev]}
            startTime={fadeStartTime}
          />
        )}

        {/* Add randomly positioned NPCs */}
        {npcs.map((npc, index) => (
          <Npc 
            key={`npc-${index}`}
            position={npc.position}
            rotation={[0, npc.rotation, 0]}
            isEven={npc.isEven}
          />
        ))}

        {cornerIndexCurrent >= 0 &&  <>
            <DJBooth cornerIndexCurrent={cornerIndexCurrent} restrictedRadius={restrictedRadius} />
          </>
        }

      </mesh>
    )
  }

  function Npc({position = [0, 0, 0], rotation = [0, Math.PI, 0], isEven}){
    const npcOpacity = 0.07;
    const npcColor = "#00ffaa"; 
    return (
      <group position={position} rotation={rotation}>

        {/* Head triangle */}
        <mesh position={[0, 2.8, 0]} rotation={[0, 0, Math.PI]} scale={[1.5, 1, 1]}>
          <shapeGeometry args={[
            new THREE.Shape([
              new THREE.Vector2(0, 0.5),    // Top point
              new THREE.Vector2(-0.4, -0.5), // Bottom left
              new THREE.Vector2(0.4, -0.5)   // Bottom right
            ])
          ]} />
          <meshBasicMaterial color={npcColor} side={THREE.DoubleSide} transparent opacity={npcOpacity}/>
        </mesh>

        {/* Body triangle */}
        <mesh position={[0, 1, 0]} rotation={[ isEven ? 0 : Math.PI, 0, Math.PI]} scale={[3, 2.5, 1]}>
          <shapeGeometry args={[
            new THREE.Shape([
              new THREE.Vector2(0, 0.5),    // Top point
              new THREE.Vector2(-0.4, -0.5), // Bottom left
              new THREE.Vector2(0.4, -0.5)   // Bottom right
            ])
          ]} />
          <meshBasicMaterial color={npcColor} side={THREE.DoubleSide} transparent opacity={npcOpacity}/>
        </mesh>
        
      </group>
    )
  }
  
function DJBooth({ cornerIndexCurrent, restrictedRadius }) {

// Enhanced Half-Cylinder Component (formerly TriangularPrism)
function HalfCylinder() {
  return (
    <mesh position={[0,0, 0.2]} rotation={[0, Math.PI / 2, 0]} scale={[1, 2, 1]}>
      <cylinderGeometry 
        args={[
          5,      // top radius
          5,      // bottom radius
          3,      // height
          32,     // radialSegments (increased for smoother curve)
          1,      // heightSegments
          false,  // openEnded
          0,      // thetaStart - start at 0 radians
          Math.PI // thetaLength - go halfway around (π radians = half circle)
        ]} 
      />
      <meshPhysicalMaterial           color={'#ffffff'} // Use white for glass
          transparent
          opacity={0.8} // Set opacity for glass effect
          roughness={0.6} // Lower roughness for a smoother surface
          metalness={0.0} // Set metalness to 0 for a non-metallic look
          clearcoat={1.0} // Increase clearcoat for a shiny surface
          side={THREE.DoubleSide}
         />
    </mesh>
  );
}


  return (
    <mesh position={[0, 1, 0]} rotation={[0, (Math.PI / 4) + ((Math.PI / 2) * -cornerIndexCurrent), 0]}>
        <HalfCylinder />
        <mesh position={[0, -0.9, 0]}>
          <RestrictedArea restrictedRadius={restrictedRadius} />
        </mesh>

      <mesh position={[0, 0, 1.5]} scale={[2, 1, 1]}>
        <cylinderGeometry args={[2.8, 2.8, 3, 3]} />
        <meshPhysicalMaterial
          color={'#ffffff'} // Use white for glass
          transparent
          opacity={0.8} // Set opacity for glass effect
          roughness={0.1} // Lower roughness for a smoother surface
          metalness={0.0} // Set metalness to 0 for a non-metallic look
          clearcoat={1.0} // Increase clearcoat for a shiny surface
          side={THREE.DoubleSide}
        />
      </mesh>


      <group position={[0, 1.8, 1.5]} rotation={[0,0, 0]}>
              <mesh scale={[0.7, 1, 0.5]}>
              
                <cylinderGeometry args={[1, 0, 1, 4]} />
                <meshBasicMaterial color={theColor} />
                
              </mesh>
              <mesh scale={[0.7, 0.3, 0.5]} rotation={[Math.PI, 0, 0]} position={[0, 0.65, 0]}>
              
                <cylinderGeometry args={[1, 0, 1, 4]} />
                <meshBasicMaterial color={theColor} />
                
              </mesh>
                {/* Face group */}
                <group position={[0, 0.5, 0.41]}>
                  {createCircleMesh([0, 0, 0.1])}
                </group>
          </group>

    </mesh>
  );
}

